import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../Button/Button";
import Modal from "../../Modal";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  clearReferralInformation,
  FinancialReferralDebtTypesRes
} from "../../../ducks/redux/actions/FinancialReferral";
import "./style.css";

interface ReferralDebtQuestionProps extends RouteComponentProps<any> {}

interface DebtOption {
  key: string;
  text: string;
}

const ReferralDebtQuestion: React.FC<ReferralDebtQuestionProps> = (
  props: any,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const referralReasonReferenceData = useSelector(
    (state: any) => state.financialReferralReducer?.referralReasonReferenceData,
  );

  const data = useSelector(
    (state: any) => state.financialReferralReducer?.financialReferralData,
  );

  const financialData = data || {};

  const [priorityDebts, setPriorityDebts] = useState<DebtOption[]>([]);
  const [nonPriorityDebts, setNonPriorityDebts] = useState<DebtOption[]>([]);

  const referralDebtTypes = financialData?.ReferralDebtTypes;
  const reduxDebtTypes =
    Array.isArray(referralDebtTypes) && referralDebtTypes.length > 0
      ? referralDebtTypes
      : [];

  const referralPriorityDebtTypes = financialData?.ReferralPriorityDebtTypes;
  const reduxPriorityDebtTypes = Array.isArray(referralPriorityDebtTypes)
    ? referralPriorityDebtTypes
    : [];

  const referralNonPriorityDebtTypes =
    financialData?.ReferralNonPriorityDebtTypes;
  const reduxNonPriorityDebtTypes = Array.isArray(referralNonPriorityDebtTypes)
    ? referralNonPriorityDebtTypes
    : [];

  const initialDebtChecked =
    reduxDebtTypes.length > 0
      ? reduxDebtTypes
      : [...reduxPriorityDebtTypes, ...reduxNonPriorityDebtTypes];

  const coerceDebtKey = (value: any) => {
    if (typeof value === "string") {
      return value;
    }
    const key = value?.key;
    if (typeof key === "string" && key) {
      return key;
    }
    const text = value?.text;
    if (typeof text === "string" && text) {
      return text;
    }
    return "";
  };

  const initialDebtCheckedKeys = (initialDebtChecked || [])
    .map(coerceDebtKey)
    .filter(Boolean);

  const [debtChecked, setDebtChecked] = useState<string[]>(
    initialDebtCheckedKeys,
  );
  const [error, setError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const debtOptionByKey = useMemo(() => {
    const map: Record<string, DebtOption> = {};
    for (const opt of priorityDebts || []) {
      if (opt?.key) {
        map[opt.key] = opt;
      }
    }
    for (const opt of nonPriorityDebts || []) {
      if (opt?.key) {
        map[opt.key] = opt;
      }
    }
    return map;
  }, [priorityDebts, nonPriorityDebts]);

  const debtKeyByText = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of [...(priorityDebts || []), ...(nonPriorityDebts || [])]) {
      if (opt?.text && opt?.key) {
        map[opt.text] = opt.key;
      }
    }
    return map;
  }, [priorityDebts, nonPriorityDebts]);

  const priorityDebtKeys = useMemo(() => {
    return new Set((priorityDebts || []).map((x) => x.key));
  }, [priorityDebts]);

  const nonPriorityDebtKeys = useMemo(() => {
    return new Set((nonPriorityDebts || []).map((x) => x.key));
  }, [nonPriorityDebts]);

  const hasDebtOptions =
    (priorityDebts.length || 0) + (nonPriorityDebts.length || 0) > 0;

  useEffect(() => {
    if (!financialData.referralFor) {
      props.history.push("/CustomerConsent");
      return;
    }
      const referralReasons = Array.isArray(financialData.ReferralReason)
      ? financialData.ReferralReason
      : [];

    if (!referralReasons.includes("Debt")) {
      props.history.push("/referralreason");
      return;
    }

    const fetchDebtReference = async () => {
      try {
        const referenceDataToUse =
          Array.isArray(referralReasonReferenceData) &&
          referralReasonReferenceData.length > 0
            ? referralReasonReferenceData
            : null;

        if (!referenceDataToUse) {
          props.history.push("/referralreason");
          return;
        }

        const firstReference = referenceDataToUse?.[0] || null;
        const namespace = firstReference?.namespace || null;
        const keyValuePairs = namespace?.keyValues || null;

        let debtObj: any = null;

        for (const _data of Object.values(keyValuePairs || {})) {
          const obj: any = _data as any;
          if (obj?.referralReason === "Debt") {
            debtObj = obj;
            break;
          }
        }

        const additionalInfo = debtObj?.additionalInfo || null;

        const debtsTypes =
          additionalInfo?.DebtsTypes ||
          additionalInfo?.debtsTypes ||
          debtObj?.metaData ||
          [];

        const tempPriority: DebtOption[] = [];
        const tempNonPriority: DebtOption[] = [];

        for (const category of debtsTypes || []) {
          const rawCategoryKey = category?.key || "";
          const categoryKey = String(rawCategoryKey).toUpperCase();

          const rawCategoryLabel = category?.description || category?.text || "";
          const categoryLabel = String(rawCategoryLabel).toLowerCase();

          const isNonPriority =
            categoryKey === "NON_PRIORITY_DEBTS" || categoryLabel.includes("non");

          const values = Array.isArray(category?.values) ? category?.values : [];

          const filteredValues = (values || []).filter((v: any) => {
            const channels = v?.allowedChannels;
            return Array.isArray(channels) && channels.includes("P360");
          });

          if (filteredValues.length === 0) {
            continue;
          }

          for (const v of filteredValues) {
            const rawKey = v?.key || "";
            const key = String(rawKey);

            const rawLabel = v?.text || v?.name || "";
            const label = String(rawLabel);
            const text = label;

            const safeKey = key || text;
            if (!safeKey || !text) {
              continue;
            }

            if (isNonPriority) {
              if (!tempNonPriority.some(x => x.key === safeKey)) {
                tempNonPriority.push({ key: safeKey, text });
              }
            } else if (!tempPriority.some(x => x.key === safeKey)) {
              tempPriority.push({ key: safeKey, text });
            }
          }
        }

        tempPriority.sort((a, b) =>
          a.text.localeCompare(b.text, undefined, { sensitivity: "base" }),
        );
        tempNonPriority.sort((a, b) =>
          a.text.localeCompare(b.text, undefined, { sensitivity: "base" }),
        );

        setPriorityDebts(tempPriority);
        setNonPriorityDebts(tempNonPriority);

        const latestFinancialData = data || {};

        const latestReferralDebtTypes = latestFinancialData?.ReferralDebtTypes;
        const latestDebtTypes =
          Array.isArray(latestReferralDebtTypes) &&
          latestReferralDebtTypes.length > 0
            ? latestReferralDebtTypes
            : [];

        const latestReferralPriorityDebtTypes =
          latestFinancialData?.ReferralPriorityDebtTypes;
        const latestPriorityDebtTypes = Array.isArray(
          latestReferralPriorityDebtTypes,
        )
          ? latestReferralPriorityDebtTypes
          : [];

        const latestReferralNonPriorityDebtTypes =
          latestFinancialData?.ReferralNonPriorityDebtTypes;
        const latestNonPriorityDebtTypes = Array.isArray(
          latestReferralNonPriorityDebtTypes,
        )
          ? latestReferralNonPriorityDebtTypes
          : [];

        const latestAll =
          latestDebtTypes.length > 0
            ? latestDebtTypes
            : [...latestPriorityDebtTypes, ...latestNonPriorityDebtTypes];

        const localTextToKey: Record<string, string> = {};
        for (const opt of [...tempPriority, ...tempNonPriority]) {
          if (opt?.text && opt?.key) {
            localTextToKey[opt.text] = opt.key;
          }
        }

        const latestKeys = (latestAll || [])
        .map((value: any) => {
          if (typeof value === "string") {
            return localTextToKey[value] || value;
          }

          const key = value?.key;
          if (typeof key === "string" && key) {
            return key;
          }

          const text = value?.text;
          if (typeof text === "string" && text) {
            return localTextToKey[text] || text;
          }

          return "";
        })
        .filter(Boolean);
        setDebtChecked(latestKeys);
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.log("Error:", e);
      }
    };

    fetchDebtReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBackHandler = () => {
    props.history.push("/referralreason");
  };

  const onContinueHandler = () => {
    if (!hasDebtOptions) {
      return;
    }
    if (hasDebtOptions && (!debtChecked || debtChecked.length === 0)) {
      setError(true);
      return;
    }

    const all = (debtChecked || [])
      .map((value: string) => {
        const direct = debtOptionByKey?.[value] || null;
        if (direct) {
          return direct;
        }

        const keyFromText = debtKeyByText?.[value] || "";
        const mapped = keyFromText ? debtOptionByKey?.[keyFromText] : null;
        if (mapped) {
          return mapped;
        }

        if (!value) {
          return null;
        }

        return { key: keyFromText || value, text: value };
      })
      .filter((x): x is DebtOption => Boolean(x));

    const priority = (all || []).filter((x: DebtOption) =>
      priorityDebtKeys.has(x.key),
    );
    const nonPriority = (all || []).filter((x: DebtOption) =>
      nonPriorityDebtKeys.has(x.key),
    );

    dispatch(
      FinancialReferralDebtTypesRes({
        all,
        priority,
        nonPriority,
      }),
    );
   
    props.history.push("/nospform");
  };

  const exitButtonHandler = () => {
    dispatch(clearReferralInformation({}));
    props.history.push("/Customer360View");
  };

  const handleChange = (event: any) => {
    setError(false);

    const updatedAll = [...(debtChecked || [])];
    const name = event?.target?.name ?? "";
    const checked = event?.target?.checked ?? false;

    if (checked) {
      if (!updatedAll.includes(name)) {
        updatedAll.push(name);
      }
    } else {
      const index = updatedAll.indexOf(name);
      if (index > -1) {
        updatedAll.splice(index, 1);
      }
    }

    setDebtChecked(updatedAll);

    const all = (updatedAll || [])
      .map((value: string) => {
        const direct = debtOptionByKey?.[value] || null;
        if (direct) {
          return direct;
        }

        const keyFromText = debtKeyByText?.[value] || "";
        const mapped = keyFromText ? debtOptionByKey?.[keyFromText] : null;
        if (mapped) {
          return mapped;
        }

        if (!value) {
          return null;
        }

        return { key: keyFromText || value, text: value };
      })
      .filter((x): x is DebtOption => Boolean(x));

    const priority = (all || []).filter((x: DebtOption) =>
      priorityDebtKeys.has(x.key),
    );
    const nonPriority = (all || []).filter((x: DebtOption) =>
      nonPriorityDebtKeys.has(x.key),
    );

    dispatch(
      FinancialReferralDebtTypesRes({
        all,
        priority,
        nonPriority,
      }),
    );
  };

  const showPriority = (priorityDebts.length || 0) > 0;
  const showNonPriority = (nonPriorityDebts.length || 0) > 0;

  const priorityColClass = showNonPriority ? "col-md-3" : "col-md-12";
  const nonPriorityColClass = showPriority ? "col-md-9" : "col-md-12";
  const isNonInputTarget = (e: any) =>
    (e?.target as HTMLElement | null)?.tagName !== "INPUT";

  const onDebtLabelClick = (e: any) => {
    if (isNonInputTarget(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onDebtLabelMouseDown = (e: any) => {
    if (isNonInputTarget(e)) {
      e.preventDefault();
    }
  };

  return (
    <div className="container sm-ml2-mr1 ctn-fht" id="referaldebtquestion-container" data-testid="referaldebtquestion-container">
      <div id="backmodal-div1" className="mb-2 md:mb-2 lg:mb-2">
        <p id="backmodal-p" className="text-teal text-xl mt-4 mb-8">
          <button
            data-testid="custBackBtn"
            className="back-btn"
            onClick={onBackHandler}
          >
            <i className="fas fa-angle-left"></i>
            <span>{t("back")}</span>
          </button>
        </p>
      </div>

      <div className="sm-ml2-mr1">
        <div className="h5" id="repreason-div1">
          <h1 className="mt-2 text-purple-100 text-2xl mb-2 global-txt ms-0 ms-md-2">
            {t("Financial_DebtQuestion_Title")}
          </h1>
        </div>
      </div>

      {error && (
        <div className="mt-2 mb-2 ms-0 ms-md-2">
          <p className="debt-error-text sm-ml2-mr1">
            {t("Financial_DebtQuestion_Error_SelectReasons")}
          </p>
        </div>
      )}

      <div className={"sm-ml2-mr1 " + (error ? "debt-error" : "")}>
        <p className="w-full text-left my-2 font-AvantGardeGothic-Md mt-3 mb-4 ms-0 ms-md-2">
          {t("Financial_DebtQuestion_TickAll")}
        </p>

        <div className="row">
          {showPriority && (
            <div className={priorityColClass + " mb-4"}>
              <h2 className="text-purple-100 text-xl global-txt mb-0 ms-0 ms-md-2">
                {t("Financial_DebtQuestion_PriorityDebts")}
              </h2>

              <Box>
                <FormControl sx={{ m: { xs: 0, md: 1 } }} variant="standard">
                  <FormGroup>
                    {priorityDebts.map((item: DebtOption) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            className="py-1"
                            checked={(debtChecked || []).includes(item.key)}
                            onChange={handleChange}
                            name={item.key}
                          />
                        }
                        label={item.text}
                        onClick={onDebtLabelClick}
                        onMouseDown={onDebtLabelMouseDown}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </div>
          )}

          {showNonPriority && (
            <div className={nonPriorityColClass + " mb-4"}>
              <h2 className="text-purple-100 text-xl global-txt mb-0 ms-0 ms-md-2">
                {t("Financial_DebtQuestion_NonPriorityDebts")}
              </h2>

              <Box>
                <FormControl sx={{ m: { xs: 0, md: 1 } }} variant="standard">
                  <FormGroup>
                    {nonPriorityDebts.map((item: DebtOption) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            className="py-1"
                            checked={(debtChecked || []).includes(item.key)}
                            onChange={handleChange}
                            name={item.key}
                          />
                        }
                        label={item.text}
                        onClick={onDebtLabelClick}
                        onMouseDown={onDebtLabelMouseDown}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </div>
          )}
        </div>
      </div>

      {/* Exit and Continue button row with modal */}
      <div className="flex justify-between items-center pb-4 md:pb-8 px-2" id="debt-btn-row">
        <button
          type="button"
          className="exit-btn link-like-btn"
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0, color: "#007bff", textDecoration: "underline" }}
          onClick={() => setIsOpen(true)}
          aria-label={t("Exit")}
        >
          {t("Exit")}
        </button>
        <Button
          id="SMS"
          type="button"
          data-testid="SMS-Button"
          name="Continue"
          onClick={onContinueHandler}
          value="Continue"
          className="button-class"
          disabled={!hasDebtOptions}
        >
          {t("Continue")}
          <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
        </Button>
      </div>

      <Modal open={isOpen} cssClasses="modalPopUp">
        <div id="exitmodal-div1" className="modalPopUpContainer container my-1">
          <div
            id="exitmodal-div2"
            className="py-lg-0 py-sm-0 h5 my-5 question-font-size text-center global-txt"
          >
            {t("SSR030ai")}
            <br />
            <br />
            {t("SSR030aii")}
          </div>
          <div
            id="exitmodal-div3"
            className="modalButtonContainer button-positions flex flex-wrap text-center pad-bot pt-lg-0"
          >
            <Button id="exitmodal-btn2" className="button-class" onClick={() => setIsOpen(false)}>
              {t("Financial_ReturnToMyReferral")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
            </Button>
            <Button id="exitmodal-btn3" onClick={exitButtonHandler} className="button-class">
              {t("leave_button")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReferralDebtQuestion;
